<template>
    <div :class="['purchase-order-filters', { dark: theme === THEMES.DARK }]">
        <div class="search-control">
            <i class="material-icons" aria-hidden="true">search</i>
            <input
                type="text"
                v-model="searchValue"
                @input="onSearchChange"
                placeholder="Search purchase orders..."
                aria-label="Search purchase orders"
                class="search-input"
            />
        </div>

        <details ref="filtersMenu" class="filters-menu">
            <summary class="filters-trigger">
                <i class="material-icons" aria-hidden="true">filter_list</i>
                <span>Filters</span>
                <span v-if="activeFilterCount" class="filter-count">{{ activeFilterCount }}</span>
                <i class="material-icons chevron" aria-hidden="true">expand_more</i>
            </summary>

            <div class="filters-popover">
                <div class="popover-header">
                    <div>
                        <strong>Filter purchase orders</strong>
                        <span>Narrow the current {{ showStatusFilter ? 'list' : 'tab' }}</span>
                    </div>
                    <button
                        v-if="activeFilterCount"
                        type="button"
                        class="clear-btn"
                        @click="clearFilters"
                    >
                        Clear filters
                    </button>
                </div>

                <div class="filter-fields">
                    <label v-if="showStatusFilter" class="filter-field">
                        <span>Status</span>
                        <select
                            v-model="statusValue"
                            @change="onStatusChange"
                            class="filter-input"
                        >
                            <option value="">All statuses</option>
                            <option value="1">Submitted</option>
                            <option value="2">Completed</option>
                            <option value="3">Cancelled</option>
                        </select>
                    </label>

                    <label class="filter-field">
                        <span>Supplier</span>
                        <select
                            v-model="supplierValue"
                            @change="onSupplierChange"
                            class="filter-input"
                        >
                            <option value="">All suppliers</option>
                            <option
                                v-for="supplier in suppliers"
                                :key="supplier.id"
                                :value="supplier.id"
                            >
                                {{ supplier.name || supplier.supplier_name }}
                            </option>
                        </select>
                    </label>

                    <label class="filter-field">
                        <span>Warehouse</span>
                        <select
                            v-model="warehouseValue"
                            @change="onWarehouseChange"
                            class="filter-input"
                        >
                            <option value="">All warehouses</option>
                            <option
                                v-for="warehouse in warehouses"
                                :key="warehouse.id"
                                :value="warehouse.id"
                            >
                                {{ warehouse.title || warehouse.name }}
                            </option>
                        </select>
                    </label>

                    <label class="filter-field">
                        <span>Date range</span>
                        <select
                            v-model="selectedTimeFrame"
                            @change="onTimeFrameChange"
                            class="filter-input"
                        >
                            <option value="">Any date</option>
                            <option
                                v-for="option in timeFrameOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </label>
                </div>

                <div v-if="selectedTimeFrame === 'custom'" class="custom-date-group">
                    <label class="filter-field">
                        <span>Start date</span>
                        <input
                            type="date"
                            v-model="customStartDate"
                            @change="updateCustomRange"
                            class="filter-input"
                            :max="customEndDate"
                        />
                    </label>
                    <label class="filter-field">
                        <span>End date</span>
                        <input
                            type="date"
                            v-model="customEndDate"
                            @change="updateCustomRange"
                            class="filter-input"
                            :min="customStartDate"
                        />
                    </label>
                </div>
            </div>
        </details>

        <div v-if="activeFilterChips.length" class="active-filter-chips" aria-label="Active filters">
            <button
                v-for="chip in activeFilterChips"
                :key="chip.key"
                type="button"
                class="active-filter-chip"
                :aria-label="`Remove ${chip.label} filter`"
                @click="removeFilter(chip.key)"
            >
                <span>{{ chip.label }}</span>
                <i class="material-icons" aria-hidden="true">close</i>
            </button>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex"
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from "date-fns"

export default {
    name: "PurchaseOrderFilters",
    props: {
        search: { type: String, default: "" },
        status: { type: String, default: "" },
        supplier: { type: String, default: "" },
        warehouse: { type: String, default: "" },
        timeFrame: { 
            type: Object, 
            default: () => ({
                startDate: '',
                endDate: '',
                period: ''
            })
        },
        suppliers: { type: Array, default: () => [] },
        showStatusFilter: { type: Boolean, default: true },
    },
    data() {
        return {
            searchValue: this.search,
            statusValue: this.status,
            supplierValue: this.supplier,
            warehouseValue: this.warehouse,
            searchTimeout: null,
            selectedTimeFrame: this.timeFrame.period || '',
            customStartDate: this.timeFrame.startDate || '',
            customEndDate: this.timeFrame.endDate || '',
            timeFrameOptions: [
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'last7days', label: 'Last 7 Days' },
                { value: 'last30days', label: 'Last 30 Days' },
                { value: 'last90days', label: 'Last 90 Days' },
                { value: 'thisMonth', label: 'This Month' },
                { value: 'lastMonth', label: 'Last Month' },
                { value: 'last3months', label: 'Last 3 Months' },
                { value: 'last6months', label: 'Last 6 Months' },
                { value: 'thisYear', label: 'This Year' },
                { value: 'lastYear', label: 'Last Year' },
                { value: 'custom', label: 'Custom Range' }
            ]
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses"]),
        activeFilterCount() {
            return [
                this.showStatusFilter && this.statusValue,
                this.supplierValue,
                this.warehouseValue,
                this.selectedTimeFrame,
            ].filter(Boolean).length
        },
        activeFilterChips() {
            const chips = []

            if (this.showStatusFilter && this.statusValue) {
                const statusLabels = { "1": "Submitted", "2": "Completed", "3": "Cancelled" }
                chips.push({ key: "status", label: `Status: ${statusLabels[this.statusValue] || this.statusValue}` })
            }

            if (this.supplierValue) {
                const supplier = this.suppliers.find((item) => String(item.id) === String(this.supplierValue))
                chips.push({
                    key: "supplier",
                    label: `Supplier: ${supplier ? supplier.name || supplier.supplier_name : this.supplierValue}`,
                })
            }

            if (this.warehouseValue) {
                const warehouse = this.warehouses.find((item) => String(item.id) === String(this.warehouseValue))
                chips.push({
                    key: "warehouse",
                    label: `Warehouse: ${warehouse ? warehouse.title || warehouse.name : this.warehouseValue}`,
                })
            }

            if (this.selectedTimeFrame) {
                const dateOption = this.timeFrameOptions.find((item) => item.value === this.selectedTimeFrame)
                chips.push({
                    key: "date",
                    label: `Date: ${dateOption ? dateOption.label : "Custom range"}`,
                })
            }

            return chips
        },
    },
    watch: {
        search(newVal) {
            this.searchValue = newVal
        },
        status(newVal) {
            this.statusValue = newVal
        },
        supplier(newVal) {
            this.supplierValue = newVal
        },
        warehouse(newVal) {
            this.warehouseValue = newVal
        },
        timeFrame: {
            handler(newVal) {
                if (newVal) {
                    this.selectedTimeFrame = newVal.period || ''
                    this.customStartDate = newVal.startDate || ''
                    this.customEndDate = newVal.endDate || ''
                }
            },
            deep: true
        }
    },
    mounted() {
        document.addEventListener("click", this.onDocumentClick)
    },
    beforeDestroy() {
        document.removeEventListener("click", this.onDocumentClick)
        clearTimeout(this.searchTimeout)
    },
    methods: {
        closeMenu() {
            if (this.$refs.filtersMenu) {
                this.$refs.filtersMenu.removeAttribute("open")
            }
        },

        onDocumentClick(event) {
            if (!this.$el.contains(event.target)) {
                this.closeMenu()
            }
        },

        removeFilter(key) {
            if (key === "status") {
                this.statusValue = ""
                this.$emit('update:status', "")
                this.emitChange()
                return
            }

            if (key === "supplier") {
                this.supplierValue = ""
                this.$emit('update:supplier', "")
                this.emitChange()
                return
            }

            if (key === "warehouse") {
                this.warehouseValue = ""
                this.$emit('update:warehouse', "")
                this.emitChange()
                return
            }

            if (key === "date") {
                this.selectedTimeFrame = ""
                this.customStartDate = ""
                this.customEndDate = ""
                this.emitTimeFrameChange({ startDate: "", endDate: "", period: "" })
            }
        },

        onSearchChange() {
            // Debounce search input
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.$emit('update:search', this.searchValue)
                this.emitChange()
            }, 300)
        },
        
        onStatusChange() {
            this.$emit('update:status', this.statusValue)
            this.emitChange()
            this.closeMenu()
        },
        
        onSupplierChange() {
            this.$emit('update:supplier', this.supplierValue)
            this.emitChange()
            this.closeMenu()
        },
        
        onWarehouseChange() {
            this.$emit('update:warehouse', this.warehouseValue)
            this.emitChange()
            this.closeMenu()
        },
        
        onTimeFrameChange() {
            this.calculateDateRange()
            if (this.selectedTimeFrame !== 'custom') {
                this.closeMenu()
            }
        },
        
        calculateDateRange() {
            const today = new Date()
            let startDate, endDate
            
            switch (this.selectedTimeFrame) {
                case 'today':
                    startDate = endDate = today
                    break
                case 'yesterday':
                    startDate = endDate = subDays(today, 1)
                    break
                case 'last7days':
                    startDate = subDays(today, 6)
                    endDate = today
                    break
                case 'last30days':
                    startDate = subDays(today, 29)
                    endDate = today
                    break
                case 'last90days':
                    startDate = subDays(today, 89)
                    endDate = today
                    break
                case 'thisMonth':
                    startDate = startOfMonth(today)
                    endDate = endOfMonth(today)
                    break
                case 'lastMonth':
                    const lastMonth = subMonths(today, 1)
                    startDate = startOfMonth(lastMonth)
                    endDate = endOfMonth(lastMonth)
                    break
                case 'last3months':
                    startDate = subMonths(today, 2)
                    endDate = today
                    break
                case 'last6months':
                    startDate = subMonths(today, 5)
                    endDate = today
                    break
                case 'thisYear':
                    startDate = startOfYear(today)
                    endDate = endOfYear(today)
                    break
                case 'lastYear':
                    const lastYear = subYears(today, 1)
                    startDate = startOfYear(lastYear)
                    endDate = endOfYear(lastYear)
                    break
                case 'custom':
                    // Custom dates will be handled by updateCustomRange
                    return
                default:
                    // Clear date range
                    this.emitTimeFrameChange({
                        startDate: '',
                        endDate: '',
                        period: ''
                    })
                    return
            }
            
            this.emitTimeFrameChange({
                startDate: format(startDate, 'yyyy-MM-dd'),
                endDate: format(endDate, 'yyyy-MM-dd'),
                period: this.selectedTimeFrame
            })
        },
        
        updateCustomRange() {
            if (this.customStartDate && this.customEndDate) {
                this.emitTimeFrameChange({
                    startDate: this.customStartDate,
                    endDate: this.customEndDate,
                    period: 'custom'
                })
                this.closeMenu()
            }
        },
        
        emitTimeFrameChange(timeFrame) {
            this.$emit('timeframe-change', timeFrame)
        },
        
        emitChange() {
            this.$emit('change', {
                search: this.searchValue,
                status: this.statusValue,
                supplier: this.supplierValue,
                warehouse: this.warehouseValue
            })
        },
        
        clearFilters() {
            const retainedTabStatus = this.showStatusFilter ? "" : this.status
            this.searchValue = ""
            this.statusValue = retainedTabStatus
            this.supplierValue = ""
            this.warehouseValue = ""
            this.selectedTimeFrame = ""
            this.customStartDate = ""
            this.customEndDate = ""
            
            this.$emit('update:search', "")
            this.$emit('update:status', retainedTabStatus)
            this.$emit('update:supplier', "")
            this.$emit('update:warehouse', "")
            this.emitTimeFrameChange({
                startDate: '',
                endDate: '',
                period: ''
            })
            this.emitChange()
            this.closeMenu()
        }
    }
}
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.purchase-order-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    position: relative;
}

.search-control {
    display: flex;
    align-items: center;
    width: min(100%, 320px);
    height: 38px;
    padding: 0 11px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 7px;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;

    &:focus-within {
        border-color: $primaryColor;
        box-shadow: 0 0 0 2px rgba($primaryColor, 0.1);
    }

    .material-icons { font-size: 18px; color: rgba($textColor, 0.48); }
}

.search-input {
    width: 100%;
    height: 100%;
    padding: 0 8px;
    border: 0;
    outline: 0;
    background: transparent;
    color: $textColor;
    font-size: 0.82rem;
}

.filters-menu { position: static; }
.filters-menu > summary { list-style: none; }
.filters-menu > summary::-webkit-details-marker { display: none; }

.filters-trigger {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 38px;
    padding: 0 11px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 7px;
    color: $primaryColor;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 700;
    user-select: none;

    .material-icons { font-size: 18px; }
    .chevron { font-size: 17px; transition: transform 0.16s ease; }

    &:hover { border-color: rgba($primaryColor, 0.45); }
    &:focus-visible { outline: 2px solid $secondaryColor; outline-offset: 2px; }
}

.filters-menu[open] {
    .filters-trigger {
        border-color: $primaryColor;
        box-shadow: 0 0 0 2px rgba($primaryColor, 0.08);
    }
    .chevron { transform: rotate(180deg); }
}

.filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: $secondaryColor;
    color: #fff;
    font-size: 0.7rem;
}

.active-filter-chips {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
}

.active-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    min-height: 30px;
    max-width: 220px;
    padding: 4px 7px 4px 10px;
    border: 1px solid rgba($secondaryColor, 0.34);
    border-radius: 999px;
    background: rgba($secondaryColor, 0.09);
    color: $primaryColor;
    cursor: pointer;
    font-size: 0.72rem;
    font-weight: 700;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .material-icons { flex: 0 0 auto; font-size: 14px; }

    &:hover {
        background: rgba($secondaryColor, 0.16);
        border-color: rgba($secondaryColor, 0.55);
    }

    &:focus-visible {
        outline: 2px solid $secondaryColor;
        outline-offset: 2px;
    }
}

.filters-popover {
    position: absolute;
    z-index: 20;
    top: calc(100% + 8px);
    left: 0;
    width: min(720px, 100%);
    max-height: min(480px, calc(100vh - 180px));
    overflow-y: auto;
    padding: 16px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 10px;
    box-shadow: 0 14px 36px rgba($textColor, 0.18);
}

.popover-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;

    strong { display: block; color: $primaryColor; font-size: 0.9rem; }
    span { display: block; margin-top: 2px; color: rgba($textColor, 0.58); font-size: 0.72rem; }
}

.filter-fields,
.custom-date-group {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}

.custom-date-group {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 12px;
}

.filter-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;

    > span {
        color: rgba($textColor, 0.72);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.02em;
    }
}

.filter-input {
    width: 100%;
    min-height: 38px;
    padding: 8px 10px;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    background: $lightCardBg;
    color: $textColor;
    font-size: 0.8rem;

    &:focus {
        outline: none;
        border-color: $primaryColor;
        box-shadow: 0 0 0 2px rgba($primaryColor, 0.1);
    }
}

.clear-btn {
    padding: 6px 9px;
    border: 0;
    background: transparent;
    color: $secondaryColor;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 700;

    &:hover { text-decoration: underline; }
}

.dark {
    .search-control,
    .filters-trigger,
    .filters-popover,
    .filter-input {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }

    .search-input { color: $darkTextColor; }
    .filters-trigger,
    .popover-header strong { color: $secondaryColorDark; }
    .filter-field > span,
    .popover-header span { color: rgba($darkTextColor, 0.7); }
    .active-filter-chip { color: $secondaryColorDark; }
}

@media (max-width: 700px) {
    .purchase-order-filters { align-items: stretch; }
    .search-control { flex: 1; width: auto; min-width: 0; }
    .active-filter-chips { display: none; }
    .filters-popover { width: 100%; }
    .filter-fields { grid-template-columns: 1fr; }
}
</style> 
